<script setup lang="ts">
import LoadingModal from "@/components/LoadingModal.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledTable from "@/components/StyledTable.vue";
import useDepartments from "@/composables/useDepartments";
import type { Department } from "@/interfaces/department";
import FieldSet from "@/modules/companies/components/FieldSet.vue";
import useCompanyDepartment from "@/modules/companies/composables/useCompanyDepartment";
import type { CompanyEditTab } from "@/modules/companies/interfaces/companyEditTab";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import type { AxiosError } from "axios";
import { reactive, ref, watch } from "vue";
import { useToast } from "vue-toastification";

interface Props extends CompanyEditTab {}
const props = defineProps<Props>();

const {
  createDepartmentAsync,
  createDepartmentLoading,
  departments,
  isLoading,
  refetch,
} = useCompanyDepartment({
  params: {
    companyId: props.company.id,
  },
  immediate: true,
});

const { updateDepartmentAsync, deleteDepartmentAsync } = useDepartments({
  params: {},
  immediate: false,
});

const invalidateQueryData = async () => {
  refetch();
};

const toast = useToast();
const createDepartment = reactive({
  name: "",
});

const headers = {
  id: "Id",
  name: "Nombre",
};
const editDepartment = ref({ id: "", name: "" });
const editableFields = ["name"];
const selectedEntry = ref<Department | undefined>();

const checkSelected = (entry: Department) => {
  return selectedEntry.value?.id === entry.id;
};

const rules = {
  name: {
    required: helpers.withMessage(
      "El nombre del departamento es requerido",
      required,
    ),
  },
};

const v$ = useVuelidate(rules, createDepartment);

const handleCreateDepartment = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message, {
        timeout: 2500,
      });
      return;
    }
  }

  try {
    await createDepartmentAsync({
      params: { ...createDepartment, companyId: props.company.id },
    });

    toast.success("Departamento creado correctamente");
    createDepartment.name = "";
    await invalidateQueryData();
  } catch (err: AxiosError | any) {
    toast.error(err.response.data.message);
  }
};

const handleDeleteDepartment = async (departmentId: string | number) => {
  try {
    await deleteDepartmentAsync({
      params: { id: Number(departmentId) },
      config: {
        onSuccessToast: "Departamento eliminado correctamente",
        raiseToast: false,
      },
    });

    toast.success("Departamento eliminado correctamente");
  } catch (err: AxiosError | any) {
    toast.error(err?.response?.data?.message);
  } finally {
    await invalidateQueryData();
  }
};

const handleSaveDepartment = async () => {
  try {
    await updateDepartmentAsync({
      params: {
        ...editDepartment.value,
        id: Number(editDepartment.value.id),
      },
      config: {
        onSuccessToast: "Departamento actualizado correctamente",
        onErrorToast: "Error al actualizar el departamento",
        raiseToast: true,
      },
    });
  } catch (err: AxiosError | any) {
    toast.error(err.response.data.message, {
      timeout: 2500,
    });
  } finally {
    await invalidateQueryData();
    selectedEntry.value = undefined;
  }
};

watch(selectedEntry, (value) => {
  if (value) {
    editDepartment.value = { ...value, id: value.id.toString() };
  }
});

watch(
  () => props.company,
  () => {
    selectedEntry.value = undefined;
  },
);
</script>

<template>
  <StyledTable
    title="Departamentos"
    create-toggle-title="Agregar departamento"
    on-delete-title="¿Está seguro de eliminar este departamento?"
    on-delete-message="Los usuarios que pertenecen a este departamento dejaran de pertenecer a el."
    :headers="headers"
    :entries="departments || []"
    :selected-entry="selectedEntry"
    :set-selected="
      (entry: Department | undefined) => {
        selectedEntry = entry;
      }
    "
    permission-to-create="create-departments"
    permission-to-edit="update-departments"
    permission-to-delete="delete-departments"
    :on-create="handleCreateDepartment"
    :on-delete="handleDeleteDepartment"
    :on-update="handleSaveDepartment"
    :on-save="handleSaveDepartment"
    :entity-type="'App\\Models\\Company'"
  >
    <template #form>
      <FieldSet class="!gap-x-[2.25rem] flex-1 !grid-cols-1">
        <StyledInput
          v-model="createDepartment.name"
          label="Nombre"
          placeholder="ej. Seguridad"
        />
      </FieldSet>

      <StyledButton
        class="bg-[#253C70] text-white h-fit ml-auto !p-[0.65rem] !py-[0.25rem]"
      >
        <span class="material-symbols-outlined text-2xl"> save </span>
        <p>Guardar</p>
      </StyledButton>
    </template>

    <template #entry="{ entry, entryField, key, handleSave }">
      <input
        v-if="editableFields.includes(key) && checkSelected(entry)"
        v-model="editDepartment[key as keyof typeof editDepartment]"
        type="text"
        class="text-xs ring-1 ring-gray-600 rounded-sm p-0.5"
        @keydown="($event) => $event.key === 'Enter' && handleSave()"
      />

      <span v-else class="">{{ entryField }}</span>
    </template>
  </StyledTable>

  <LoadingModal :when="isLoading" />
</template>

<style scoped>
.grid-set {
  @apply grid-cols-[repeat(4,_minmax(144px,_1fr))];
}
</style>
